/**
 * What the donor saw, carried on DONATE (CON-316, SPEC 3.C4, §2.2, §2.3, §2.5).
 *
 * Framework-free, so every DONATE builder and its tests share one copy. Money
 * goes through CON-310 (`@charitystack/shared/currency`) only: rates stay
 * strings, and a USD figure is built in integer cents and turned into a number
 * once, at the end. Never `display / parseFloat(rate)`, never a float product:
 * checkout's rounded-ticket guard (CON-317) refuses an amount that is not in
 * whole cents, so 19 × 26.37 = 501.03000000000003 would be charged at stored
 * prices instead (C-A34).
 *
 * THE CONTRACT
 *   - `amount` stays the USD NUMBER it is today; the builders set it per the
 *     §2.3 per-form rule, and nothing here changes a field that exists today.
 *   - The five fields go on together or not at all, after every existing key,
 *     then `fxRatesDate` (the rates response's Date header, D-R39). Absent means
 *     USD, not converted, and the message is byte-identical to today.
 *   - Never on UPDATE_CART, a scheduled gift or a $0 total.
 *   - A converted context that is missing a field throws: posting a display
 *     figure as USD is worse than an error the donor can see (critic F8).
 */
import {
  fromMinorUnits,
  minorUnits,
  snapUnit,
  toDisplay,
  toMinorUnits,
  toUsd,
} from '@charitystack/shared/currency';

const USD = 'USD';

/** The five DONATE fields, in the order they are written. `fxRatesDate` rides with them but is not one of them. */
export const DISPLAY_SNAPSHOT_DONATE_FIELDS = Object.freeze([
  'displayCurrency',
  'displayGiftAmount',
  'fxRate',
  'fxRateTableId',
  'fxRateAsOf',
]);

/** Config keys that must never reach a posted DONATE (§2.5; SPEC 3.C4). */
const CONFIG_ONLY_KEYS = Object.freeze(['currencySettings', 'acceptsRoundedTicketPrices']);

const SNAPSHOT_CONTEXT_KEYS = Object.freeze([
  'displayCurrency',
  'fxRate',
  'fxRateTableId',
  'fxRateAsOf',
  'fxRatesDate',
]);

const isNonEmptyString = (value) => typeof value === 'string' && value.length > 0;

function snapshotError(message) {
  return new Error(`CON-316 display snapshot: ${message}`);
}

/**
 * The snapshot a converted context carries, or null when the donor sees USD.
 * Throws when a converted context lacks any field (CON-313's invariant makes
 * that unreachable; loud beats charging a display figure).
 */
export function snapshotOf(ctx) {
  if (ctx?.converted !== true) return null;
  for (const key of SNAPSHOT_CONTEXT_KEYS) {
    if (!isNonEmptyString(ctx[key])) throw snapshotError(`converted context has no ${key}`);
  }
  if (ctx.displayCurrency === USD) throw snapshotError('converted context is USD');
  return {
    displayCurrency: ctx.displayCurrency,
    fxRate: ctx.fxRate,
    fxRateTableId: ctx.fxRateTableId,
    fxRateAsOf: ctx.fxRateAsOf,
    fxRatesDate: ctx.fxRatesDate,
  };
}

/** Display figure → USD, half-up cents, as a decimal string (CON-310 toUsd). */
export function usdStringFromDisplay(display, snap) {
  return toUsd(display, snap.fxRate, snap.displayCurrency);
}

/** Display figure → the USD NUMBER `amount` carries. */
export function usdFromDisplay(display, snap) {
  return Number(usdStringFromDisplay(display, snap));
}

/** A display figure written at exactly the currency's minor units, no separators ("40" → "40.00"). */
export function normalizeDisplayGift(value, snap) {
  const currency = snap.displayCurrency;
  return fromMinorUnits(toMinorUnits(value, currency), currency);
}

/** A fixed USD figure (a pinned `?amount=`, a giving level, an exact ticket total) → display, half-up. */
export function displayFromUsd(usd, snap) {
  return toDisplay(usd, snap.fxRate, snap.displayCurrency);
}

function readQuantity(quantity) {
  if (typeof quantity !== 'number' || !Number.isInteger(quantity) || quantity < 0) {
    throw new RangeError(`a quantity must be a whole number >= 0, got ${String(quantity)}`);
  }
  return BigInt(quantity);
}

function wholeCents(unitUsd) {
  const cents = toMinorUnits(unitUsd, USD, { rounding: 'down' });
  if (cents < 0n || cents !== toMinorUnits(unitUsd, USD, { rounding: 'up' })) {
    throw new RangeError(`a unit price must be whole cents >= 0, got ${String(unitUsd)}`);
  }
  return cents;
}

/**
 * Σ quantity × unit USD over `lines` ({ unitUsd, quantity }), in integer cents,
 * divided once: 19 × 26.37 → 501.03 exactly (C-A34). A sub-cent unit throws.
 */
export function sumUnitsUsd(lines) {
  let cents = 0n;
  for (const { unitUsd, quantity } of lines) cents += wholeCents(unitUsd) * readQuantity(quantity);
  return Number(fromMinorUnits(cents, USD));
}

/**
 * Fixed prices snapped up in the display currency (D-R32, CON-310 snapUnit, each
 * unit rounded once): `usd` is Σ quantity × unit USD (a NUMBER, whole cents),
 * `display` is Σ quantity × unit display (a string at the minor units), `units`
 * the snapped unit per line. The caller chooses each line's unit.
 */
export function snappedUnitsTotal(lines, snap) {
  const currency = snap.displayCurrency;
  let usdCents = 0n;
  let displayMinor = 0n;
  const units = [];
  for (const { unitUsd, quantity } of lines) {
    const qty = readQuantity(quantity);
    const unit = snapUnit(unitUsd, snap.fxRate, currency);
    usdCents += toMinorUnits(unit.usd, USD) * qty;
    displayMinor += toMinorUnits(unit.display, currency) * qty;
    units.push(unit);
  }
  return {
    usd: Number(fromMinorUnits(usdCents, USD)),
    display: fromMinorUnits(displayMinor, currency),
    units,
  };
}

/** A future start or an explicit time (mirrors checkout's hasScheduledPayment). */
export function isScheduledDonation(data) {
  return !!data?.utcStartDateTime || (!!data?.timeValue && data.timeValue !== 'now');
}

/** The same rule per cart item. */
export function cartHasScheduledItem(items) {
  return Array.isArray(items) && items.some((item) => isScheduledDonation(item));
}

/** A display figure written exactly at the currency's minor units: "40.00", "15.000", "40000". */
function isMinorUnitString(value, currency) {
  if (typeof value !== 'string') return false;
  const decimals = minorUnits(currency);
  const pattern = decimals === 0 ? /^\d+$/ : new RegExp(`^\\d+\\.\\d{${decimals}}$`);
  return pattern.test(value);
}

/**
 * Appends the five fields, then `fxRatesDate`, after every existing key of a
 * DONATE `data`. A no-op (byte-identical) when `snap` is null or the total is $0.
 * Throws, writing nothing, when the snapshot or the gift figure is incomplete.
 */
export function attachDonateSnapshot(data, snap, displayGiftAmount) {
  if (!snap || Number(data.amount) === 0) return data;
  for (const key of SNAPSHOT_CONTEXT_KEYS) {
    if (!isNonEmptyString(snap[key])) throw snapshotError(`snapshot has no ${key}`);
  }
  if (!isMinorUnitString(displayGiftAmount, snap.displayCurrency)) {
    throw snapshotError(
      `displayGiftAmount ${String(displayGiftAmount)} is not a minor-unit string`,
    );
  }
  data.displayCurrency = snap.displayCurrency;
  data.displayGiftAmount = displayGiftAmount;
  data.fxRate = snap.fxRate;
  data.fxRateTableId = snap.fxRateTableId;
  data.fxRateAsOf = snap.fxRateAsOf;
  data.fxRatesDate = snap.fxRatesDate;
  return data;
}

// Cart storage is owned by the legacy hosted script, which copies mappedData
// onto each cart item. Keep the donor's exact per-unit display amount there so
// three-decimal currencies survive the USD-cent cart round trip. This is cart
// metadata, not the immutable DONATE snapshot above.
export const CART_DISPLAY_METADATA_KEY = '__charitystackDisplay';

export function attachCartDisplayMetadata(
  data,
  snap,
  displayUnitAmount,
  displayUnitsByProductId = null,
) {
  if (!snap) return data;
  const validUnit = isMinorUnitString(displayUnitAmount, snap.displayCurrency);
  const validProductUnits = Object.fromEntries(
    Object.entries(displayUnitsByProductId || {}).filter(([, amount]) =>
      isMinorUnitString(amount, snap.displayCurrency),
    ),
  );
  if (!validUnit && Object.keys(validProductUnits).length === 0) return data;
  data.mappedData = {
    ...(data.mappedData || {}),
    [CART_DISPLAY_METADATA_KEY]: {
      displayCurrency: snap.displayCurrency,
      fxRate: snap.fxRate,
      ...(validUnit ? { displayUnitAmount } : {}),
      ...(Object.keys(validProductUnits).length > 0
        ? { displayUnitsByProductId: validProductUnits }
        : {}),
    },
  };
  return data;
}

/** Deletes the config-only keys from a posted copy (§2.5); a no-op when neither is present. */
export function stripConfigOnlyKeys(data) {
  for (const key of CONFIG_ONLY_KEYS) {
    if (Object.prototype.hasOwnProperty.call(data, key)) delete data[key];
  }
  return data;
}

/** A ticket DONATE priced with rounded prices (SPEC §2.2, R2-B1): checkout charges `amount`. */
export function markTicketPricesRounded(data) {
  data.ticketPricesRounded = true;
  return data;
}
